import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { uniqBy } from 'lodash';
import { Spin } from 'antd';
import Heading from 'LearningManager/components/Heading';
import styles from 'LearningManager/styles/CustomSetup.module.scss';
import CheckBoxCard from 'LearningManager/components/CheckBoxCard';
import { Icon, Input, Notification } from 'Components';
import MultiSelectDropdown from 'LearningManager/components/MultiSelectDropdown';
import { useStepper } from 'LearningManager/hooks/Stepper.context';
import useAssignmentTenantId from 'LearningManager/hooks/useAssignmentTenantId';
import useAudienceTenantId from 'LearningManager/hooks/useAudienceTenantId';
import usePagedProperties from 'LearningManager/hooks/usePagedProperties';
import InfinityScrollWrapper from 'Components/InfinityScroll/InfinityScrollWrapper';
import useDebouncedValue from 'LearningManager/reports/hooks/useDebouncedValue';
import {
	SEARCH_DEBOUNCE_MS,
	toServerSearch
} from 'LearningManager/helpers/search';
import { useStores } from 'Hooks/useStore';
import { Status } from 'app-types';
import { DirectoryUser } from 'Services/Api/Directory/Types';

const LIST_SCROLL_ID = 'lms-ca-au-individual-scroll';

const toSelectedUser = (user: DirectoryUser) => ({
	id: user.id,
	firstName: user.firstName,
	lastName: user.lastName,
	email: user.email
});

const Individual = observer(() => {
	const { assignUsersStore } = useStores();
	const { users, usersTotal, hasMoreUsers, status } = assignUsersStore;
	const { tenantId, isTenantResolved } = useAudienceTenantId(
		useAssignmentTenantId()
	);
	const { dropdownProps: propertyDropdownProps } = usePagedProperties(
		tenantId,
		isTenantResolved
	);

	const {
		draft: {
			customSetup: {
				selectedIndividualUsers,
				selectedIndividualPropertyIds,
				allIndividualPropertiesSelected
			}
		},
		toggleIndividualUserSelection,
		setIndividualUserSelection,
		setAssignUsersPropertyFilter
	} = useStepper();

	const [search, setSearch] = useState('');
	const serverSearch = toServerSearch(search);
	const debouncedSearch = useDebouncedValue(serverSearch, SEARCH_DEBOUNCE_MS);
	const hasPropertyScope =
		allIndividualPropertiesSelected || selectedIndividualPropertyIds.length > 0;

	useEffect(() => {
		if (allIndividualPropertiesSelected && !isTenantResolved) return;
		const list = document.getElementById(LIST_SCROLL_ID);
		if (list) list.scrollTop = 0;
		assignUsersStore
			.fetchUsersByProperty(selectedIndividualPropertyIds, debouncedSearch, {
				allProperties: allIndividualPropertiesSelected,
				tenantId
			})
			.catch(() => undefined);
	}, [
		selectedIndividualPropertyIds,
		allIndividualPropertiesSelected,
		debouncedSearch,
		isTenantResolved,
		tenantId
	]);

	const isLoading =
		status.fetchUsers === Status.Pending || serverSearch !== debouncedSearch;
	const isLoadingMore = status.fetchMoreUsers === Status.Pending;
	const hasLoadMoreFailed = status.fetchMoreUsers === Status.Failure;
	const isSelectingAll = status.fetchAllUsers === Status.Pending;

	const loadMoreUsers = () =>
		assignUsersStore.fetchNextUsersPage().catch(() => undefined);

	const selectedIds = useMemo(
		() => new Set(selectedIndividualUsers.map((u) => u.id)),
		[selectedIndividualUsers]
	);

	const resultCount = usersTotal;

	const handleCheckboxChange = (id: number | string, checked: boolean) => {
		const uuid = String(id);
		const user = users.find((u) => u.id === uuid);
		if (!user) return;
		if (checked !== selectedIds.has(uuid)) {
			toggleIndividualUserSelection(toSelectedUser(user));
		}
	};

	const isAllSelected =
		!hasMoreUsers &&
		users.length > 0 &&
		users.every((u) => selectedIds.has(u.id));

	const handleSelectAll = async () => {
		if (isSelectingAll) return;
		if (isAllSelected) {
			const listedIds = new Set(users.map((u) => u.id));
			setIndividualUserSelection(
				selectedIndividualUsers.filter((u) => !listedIds.has(u.id))
			);
			return;
		}

		let pool = users;
		if (hasMoreUsers) {
			try {
				const allUsers = await assignUsersStore.fetchAllUsers();
				if (!allUsers) return;
				pool = allUsers;
			} catch {
				Notification.error({
					description: 'Could not load all users. Please try again.'
				});
				return;
			}
		}

		setIndividualUserSelection(
			uniqBy(
				[...selectedIndividualUsers, ...pool.map(toSelectedUser)],
				'id'
			)
		);
	};

	return (
		<div id="lms-ca-assign-users-individual">
			<div>
				<Heading />
				<div className={styles.filters}>
					<div
						id="lms-ca-au-individual-property-filter"
						className={styles.search}
					>
						<div className={styles.selectProp}>Select Property</div>
						<MultiSelectDropdown
							{...propertyDropdownProps}
							searchable
							searchPlaceholder="Search properties"
							placeholder="Select option"
							onChange={(ids, allSelected) => {
								setAssignUsersPropertyFilter(
									'selectedIndividualPropertyIds',
									ids,
									allSelected
								);
							}}
							defaultSelectedIds={selectedIndividualPropertyIds}
							defaultAllSelected={allIndividualPropertiesSelected}
						/>
					</div>
					<Input.Search
						id="lms-ca-au-individual-search"
						className={styles.search}
						placeholder="Search by name or email"
						suffix={<Icon type="search" role="img" />}
						// Keeps a suffix rendered, so the input isn't remounted mid-typing.
						allowClear
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
				</div>
			</div>
			<div className={styles.header}>
				<Heading
					titleClassName={styles.title}
					title={`Select Individuals (${resultCount} Results)`}
				/>
				{resultCount > 1 && (
					<div
						id="lms-ca-au-individual-select-all"
						className={styles.selectAllBtn}
						onClick={handleSelectAll}
					>
						{isSelectingAll
							? 'Loading users…'
							: isAllSelected
							? 'Deselect all'
							: 'Select all'}
					</div>
				)}
			</div>
			<InfinityScrollWrapper
				dataLength={users.length}
				hasMore={hasMoreUsers && !isLoading && !hasLoadMoreFailed}
				onLoadMore={loadMoreUsers}
				loading={isLoadingMore}
				scrollableId={LIST_SCROLL_ID}
				endMessageText=""
			>
			<div id="lms-ca-au-individual-list" className={styles.container}>
				{isLoading && <div className={styles.loading}><Spin /></div>}
				{!isLoading && !hasPropertyScope && (
					<div className={styles.empty}>
						Select a property to see available users.
					</div>
				)}
				{!isLoading &&
					hasPropertyScope &&
					users.length === 0 &&
					status.fetchUsers !== Status.Failure && (
						<div className={styles.empty}>No users found.</div>
					)}
				{!isLoading && status.fetchUsers === Status.Failure && (
					<div className={styles.empty}>
						Could not load users. Please try again.
					</div>
				)}
				{users.map((user) => {
					const isChecked = selectedIds.has(user.id);
					return (
						<CheckBoxCard
							key={user.id}
							id={user.id}
							htmlId={`lms-ca-au-individual-${user.id}`}
							icon={
								<Icon
									type={
										isChecked
											? 'indiOptFilled'
											: 'individualOptIcon'
									}
								/>
							}
							title={`${user.firstName} ${user.lastName}`}
							desc={user.email}
							isChecked={isChecked}
							onChange={handleCheckboxChange}
						/>
					);
				})}
				{hasMoreUsers && !isLoading && hasLoadMoreFailed && (
					<div
						id="lms-ca-au-individual-load-more"
						className={styles.loadMore}
					>
						<span className={styles.empty}>
							Could not load more users.
						</span>
						<div
							id="lms-ca-au-individual-load-more-retry"
							className={styles.selectAllBtn}
							onClick={loadMoreUsers}
						>
							Try again
						</div>
					</div>
				)}
			</div>
			</InfinityScrollWrapper>
		</div>
	);
});

export default Individual;
